// src/components/client/VideoCompressor.jsx
import React, { useState, useRef, useEffect } from 'react';
import { Upload, Download, X, Loader2, Settings, FileVideo, Gauge, CheckCircle } from 'lucide-react';

const API_URL = 'https://fbapi.tdidigital.io';

export default function VideoCompressor() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [compressionLevel, setCompressionLevel] = useState('balanced');
  const [taskId, setTaskId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('idle'); // idle, uploading, uploaded, compressing, completed, failed
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const pollInterval = useRef(null);

  const compressionOptions = {
    light: {
      label: 'Compression Légère (75%)',
      crf: 23,
      description: 'Qualité excellente, réduction ~75%',
      icon: '🟢'
    },
    balanced: {
      label: 'Compression Équilibrée (80-85%)',
      crf: 28,
      description: 'Qualité très bonne, réduction ~80-85%',
      icon: '🟡'
    },
    aggressive: {
      label: 'Compression Forte (90%+)',
      crf: 32,
      description: 'Qualité correcte, réduction >90%',
      icon: '🔴'
    }
  };

  useEffect(() => {
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, []);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type.startsWith('video/')) {
      setFile(selectedFile);
      setError(null);
      setResult(null);
      setCurrentStep('idle');
      
      const videoURL = URL.createObjectURL(selectedFile);
      setPreview(videoURL);
    } else {
      setError('Veuillez sélectionner un fichier vidéo valide');
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    setCurrentStep('uploading');
    setUploadProgress(0);
    setError(null);

    const formData = new FormData();
    formData.append('video', file);

    try {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          setUploadProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          const response = JSON.parse(xhr.responseText);
          setTaskId(response.task_id);
          setCurrentStep('uploaded');
          console.log('✅ Upload terminé:', response);
        } else {
          const errorData = JSON.parse(xhr.responseText);
          setError(errorData.error || 'Erreur lors de l\'upload');
          setCurrentStep('failed');
        }
      });

      xhr.addEventListener('error', () => {
        setError('Erreur de connexion lors de l\'upload');
        setCurrentStep('failed');
      });

      xhr.open('POST', `${API_URL}/api/compress/upload`);
      xhr.send(formData);

    } catch (err) {
      setError(err.message || 'Erreur lors de l\'upload');
      setCurrentStep('failed');
    }
  };

  const handleStartCompression = async () => {
    if (!taskId) return;

    setCurrentStep('compressing');
    setCompressionProgress(0);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/compress/compress/${taskId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          compression_level: compressionLevel
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erreur démarrage compression');
      }

      const data = await response.json();
      console.log('✅ Compression démarrée:', data);

      // Démarrer le polling du statut
      startPolling();

    } catch (err) {
      console.error('Compression error:', err);
      setError(err.message || 'Erreur lors de la compression');
      setCurrentStep('failed');
    }
  };

  const startPolling = () => {
    pollInterval.current = setInterval(async () => {
      try {
        const response = await fetch(`${API_URL}/api/compress/status/${taskId}`);
        
        if (!response.ok) {
          throw new Error('Erreur récupération statut');
        }

        const status = await response.json();
        console.log('📊 Statut:', status);

        if (status.status === 'compressing') {
          setCompressionProgress(status.progress || 0);
        } else if (status.status === 'completed') {
          setResult(status);
          setCurrentStep('completed');
          setCompressionProgress(100);
          clearInterval(pollInterval.current);
        } else if (status.status === 'failed') {
          setError(status.error || 'Compression échouée');
          setCurrentStep('failed');
          clearInterval(pollInterval.current);
        }

      } catch (err) {
        console.error('Polling error:', err);
        setError('Erreur suivi de progression');
        clearInterval(pollInterval.current);
      }
    }, 2000); // Poll toutes les 2 secondes
  };

  const handleDownload = () => {
    if (result && result.download_url) {
      window.open(`${API_URL}${result.download_url}`, '_blank');
    }
  };

  const resetForm = () => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
    }
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setTaskId(null);
    setUploadProgress(0);
    setCompressionProgress(0);
    setCurrentStep('idle');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-slate-950 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FileVideo className="w-12 h-12 text-purple-400" />
            <h1 className="text-4xl font-bold text-white">Compresseur Vidéo</h1>
          </div>
          <p className="text-gray-400 text-lg">
            Réduisez la taille de vos vidéos jusqu'à 90% sans perte visible de qualité
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-slate-900/50 backdrop-blur-lg rounded-2xl border border-slate-800 p-8 shadow-2xl">
          
          {/* Upload Section */}
          {currentStep === 'idle' && !file && (
            <div 
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-slate-700 rounded-xl p-12 text-center cursor-pointer hover:border-purple-500 transition-all duration-300 hover:bg-slate-800/30"
            >
              <Upload className="w-16 h-16 mx-auto mb-4 text-purple-400" />
              <p className="text-white text-lg mb-2">
                Cliquez pour sélectionner une vidéo
              </p>
              <p className="text-gray-400 text-sm">
                MP4, AVI, MOV, MKV jusqu'à 500MB
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* File Selected - Ready to Upload */}
          {file && currentStep === 'idle' && (
            <div className="space-y-6">
              <div className="bg-slate-800/50 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileVideo className="w-10 h-10 text-purple-400" />
                  <div>
                    <p className="text-white font-medium">{file.name}</p>
                    <p className="text-gray-400 text-sm">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  onClick={resetForm}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {preview && (
                <div className="rounded-lg overflow-hidden">
                  <video 
                    src={preview} 
                    controls 
                    className="w-full max-h-64 bg-black"
                  />
                </div>
              )}

              <button
                onClick={handleUpload}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium py-4 rounded-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <Upload className="w-5 h-5" />
                Uploader vers le serveur
              </button>
            </div>
          )}

          {/* Uploading Progress */}
          {currentStep === 'uploading' && (
            <div className="space-y-4">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-blue-400 mx-auto mb-4" />
                <p className="text-white text-lg font-medium mb-2">Upload en cours...</p>
                <p className="text-gray-400 text-3xl font-bold">{uploadProgress}%</p>
              </div>
              
              <div className="w-full bg-slate-800 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 h-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Uploaded - Choose Compression Level */}
          {currentStep === 'uploaded' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Upload réussi ! ✅
                </h3>
                <p className="text-gray-400">
                  Fichier prêt pour la compression
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-white font-medium mb-4">
                  <Settings className="w-5 h-5" />
                  Choisir le niveau de compression
                </label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {Object.entries(compressionOptions).map(([key, option]) => (
                    <button
                      key={key}
                      onClick={() => setCompressionLevel(key)}
                      className={`p-4 rounded-lg border-2 transition-all duration-200 text-left ${
                        compressionLevel === key
                          ? 'border-purple-500 bg-purple-500/20'
                          : 'border-slate-700 hover:border-slate-600'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-2xl">{option.icon}</span>
                        <Gauge className="w-5 h-5 text-purple-400" />
                      </div>
                      <p className="text-white font-medium mb-1">{option.label}</p>
                      <p className="text-gray-400 text-sm">{option.description}</p>
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={handleStartCompression}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium py-4 rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <FileVideo className="w-5 h-5" />
                Lancer la compression
              </button>
            </div>
          )}

          {/* Compressing Progress */}
          {currentStep === 'compressing' && (
            <div className="space-y-4">
              <div className="text-center">
                <Loader2 className="w-12 h-12 animate-spin text-purple-400 mx-auto mb-4" />
                <p className="text-white text-lg font-medium mb-2">Compression en cours...</p>
                <p className="text-gray-400 text-3xl font-bold">{compressionProgress}%</p>
              </div>
              
              <div className="w-full bg-slate-800 rounded-full h-4 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-purple-600 to-pink-600 h-full transition-all duration-300"
                  style={{ width: `${compressionProgress}%` }}
                />
              </div>

              <p className="text-center text-gray-400 text-sm">
                Cela peut prendre quelques minutes selon la taille du fichier...
              </p>
            </div>
          )}

          {/* Completed - Show Results */}
          {currentStep === 'completed' && result && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileVideo className="w-10 h-10 text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">
                  Compression réussie ! 🎉
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Taille originale</p>
                  <p className="text-white text-xl font-bold">
                    {formatFileSize(result.original_size)}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Taille compressée</p>
                  <p className="text-green-400 text-xl font-bold">
                    {formatFileSize(result.compressed_size)}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Réduction</p>
                  <p className="text-purple-400 text-xl font-bold">
                    {result.reduction_percentage}%
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={handleDownload}
                  className="bg-gradient-to-r from-green-600 to-emerald-600 text-white font-medium py-4 rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Télécharger
                </button>
                <button
                  onClick={resetForm}
                  className="bg-slate-700 text-white font-medium py-4 rounded-lg hover:bg-slate-600 transition-all duration-200"
                >
                  Nouvelle compression
                </button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4 text-red-400 mt-4">
              {error}
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
          <div className="bg-slate-900/30 backdrop-blur-lg rounded-lg p-4 border border-slate-800">
            <p className="text-purple-400 font-bold text-2xl mb-2">75-90%</p>
            <p className="text-gray-400 text-sm">Réduction de taille</p>
          </div>
          <div className="bg-slate-900/30 backdrop-blur-lg rounded-lg p-4 border border-slate-800">
            <p className="text-purple-400 font-bold text-2xl mb-2">2 Étapes</p>
            <p className="text-gray-400 text-sm">Upload puis compression</p>
          </div>
          <div className="bg-slate-900/30 backdrop-blur-lg rounded-lg p-4 border border-slate-800">
            <p className="text-purple-400 font-bold text-2xl mb-2">Rapide</p>
            <p className="text-gray-400 text-sm">Traitement optimisé</p>
          </div>
        </div>
      </div>
    </div>
  );
}